import { BRAND_NAME, CONFLICT_RESOLUTION_EMAIL } from "@app/shared/constants";
import { Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { NavLink } from "features/navigation/NavLink";

const useStyles = makeStyles((theme) => ({
    contentContainer: {
        paddingBottom: theme.spacing(14),
        [theme.breakpoints.down("sm")]: {
            paddingBottom: theme.spacing(8),
        },
    },
    subList: {
        listStyleType: "lower-alpha",
    },
}));

export const Guidelines = ({ mainHeadingVariant = "h2" }: { mainHeadingVariant?: "h1" | "h2" }) => {
    const classes = useStyles();

    return (
        <div className={classes.contentContainer}>
            <Typography variant={mainHeadingVariant}>
                Community Guidelines for Mindful and Heartful Relating
            </Typography>

            <Typography variant="h2">I agree to&hellip;</Typography>
            <Typography variant="body1" component="div">
                <ol>
                    <li>
                        Honor everyone’s unique journey, regardless of race, color, nationality,
                        sex, gender, age, religion, ability, medical conditions, military status, or
                        marital status.
                    </li>
                    <li>Support others with kindness and respect.</li>
                    <li>
                        Share from my own experience, honestly and vulnerably, using “I” statements.
                        Speak from the heart.
                    </li>
                    <li>Avoid giving unsolicited advice or trying to 'fix' another. </li>
                    <li>
                        Hold what others share in strict confidence.
                        <ol className={classes.subList}>
                            <li>
                                Avoid using AI transcription tools such as{" "}
                                <NavLink to={"https://www.read.ai/"} target="_blank">
                                    read.ai
                                </NavLink>
                                ,{" "}
                                <NavLink to={"https://www.otter.ai/"} target="_blank">
                                    otter.ai
                                </NavLink>
                                , and Zoom IQ.
                            </li>
                        </ol>
                    </li>
                    <li>
                        Encourage full participation by all present. Move back if I speak often,
                        move up if I speak less.
                    </li>
                    <li>
                        Set aside roles I might hold like coach, teacher, or therapist.
                        <ol className={classes.subList}>
                            <li>
                                Avoid promoting of products or services or asking for personal
                                favors outside the group context.
                            </li>
                        </ol>
                    </li>
                    <li>
                        To be fully present:
                        <ol className={classes.subList}>
                            <li>Arrive early to settle in for an on-time start.</li>
                            <li>Use a device with a big screen that allows me to see everyone.</li>
                            <li>
                                Keep my video on to enhance connection, and briefly switch it off if
                                I need to move.
                            </li>
                            <li>"Hide Self View" in Zoom for more natural interaction.</li>
                        </ol>
                    </li>
                    <li>
                        In breakout rooms:
                        <ol className={classes.subList}>
                            <li>Nominate a timekeeper.</li>
                            <li>Know that it's okay not to share. </li>
                            <li>
                                Chat casually once everyone has had a chance to respond to the
                                prompt.{" "}
                            </li>
                        </ol>
                    </li>
                    <li>Leave the breakout room or the session anytime I feel uncomfortable.</li>
                    <li>
                        Avoid:
                        <ol className={classes.subList}>
                            <li>Unpacking or processing active trauma, </li>
                            <li>Coping with unregulated mental illness,</li>
                            <li>Talking about causing intentional harm to myself or others.</li>
                        </ol>
                    </li>
                    <li>
                        Allow myself and others to show up imperfectly as we learn mindful relating.
                    </li>
                    <li>
                        Report any concerns or conflicts confidentially to {BRAND_NAME} (
                        <NavLink href={`mailto:${CONFLICT_RESOLUTION_EMAIL}`}>
                            {CONFLICT_RESOLUTION_EMAIL}
                        </NavLink>
                        ).
                    </li>
                </ol>
            </Typography>

            <Typography variant="h2">
                I understand that {BRAND_NAME} reserves the right to
            </Typography>
            <Typography variant="body1" component="div">
                <ul>
                    <li>
                        redirect members to seek more suitable support from a therapist or other
                        professional, if their participation here could be detrimental.
                    </li>
                    <li>remove members who repeatedly breach these guidelines.</li>
                </ul>
            </Typography>
        </div>
    );
};

export default Guidelines;
